import { useState, useEffect, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjectDevices, createDeviceInstance, updateDeviceInstance, deleteDeviceInstance } from '../api/deviceInstances'
import { getSpecification, type Specification } from '../api/specification'
import { getElectricalPanels, type ElectricalPanel } from '../api/electricalPanels'
import type { DeviceInstance } from '../types'
import { searchTextIncludes } from '../utils/searchText'

interface DeviceInstancesProps {
  projectId: number
  electricalPanelId?: number
  specificationId?: number
  hideParentColumn?: boolean
}

export default function DeviceInstances({ projectId, electricalPanelId, specificationId, hideParentColumn = false }: DeviceInstancesProps) {
  const { panelId } = useParams<{ panelId?: string }>()
  const [searchParams] = useSearchParams()
  const [selectedInstance, setSelectedInstance] = useState<DeviceInstance | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [copyInstance, setCopyInstance] = useState<DeviceInstance | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)
  const [selectedSpecificationId, setSelectedSpecificationId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterPanelId, setFilterPanelId] = useState<string>('')
  const [createFormPanelId, setCreateFormPanelId] = useState<number | null>(null)

  const isCableInstance = (instance: DeviceInstance) => instance.device_type_code === 'cable'

  // Функция для построения URL страницы экземпляра (со ссылкой на панель, если есть)
  const getInstanceUrl = (instance: DeviceInstance) => {
    const instanceId = instance.id
    const segment = isCableInstance(instance) ? 'cable-instances' : 'device-instances'
    const panelIdForLink = panelId || (instance.electrical_panel_id ? String(instance.electrical_panel_id) : null)
    if (panelIdForLink) {
      return `/projects/${projectId}/panels/${panelIdForLink}/${segment}/${instanceId}`
    }
    const specId = specificationId || searchParams.get('spec')
    if (specId && !isCableInstance(instance)) {
      return `/projects/${projectId}/device-instances/${instanceId}?spec=${specId}`
    }
    return `/projects/${projectId}/${segment}/${instanceId}`
  }

  // Показывать поиск и фильтр по щиту только на странице "все экземпляры"
  const showAllInstancesView = !electricalPanelId && !specificationId

  // Sorting state
  const [sortField, setSortField] = useState<keyof DeviceInstance | 'position_label' | 'spec_name' | 'id' | 'panel_proj_id'>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const queryClient = useQueryClient()

  // Sorting functions
  const handleSort = (field: keyof DeviceInstance | 'position_label' | 'spec_name' | 'id' | 'panel_proj_id') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Sortable header component
  const SortableHeader = ({ field, children }: { field: keyof DeviceInstance | 'position_label' | 'spec_name' | 'id' | 'panel_proj_id', children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <span className="flex flex-col">
          <span className={`text-xs ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}>▲</span>
          <span className={`text-xs ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}>▼</span>
        </span>
      </div>
    </th>
  )

  // Close modals on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCreateForm(false)
        setShowEditForm(false)
        setCopyInstance(null)
        setSelectedInstance(null)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setCopyInstance(null)
    setSelectedInstance(null)
    setMouseDownTarget(null)
    setSelectedSpecificationId(null)
    setCreateFormPanelId(null)
  }

  const searchActive = showAllInstancesView && searchQuery.trim().length > 0

  // Load device instances for the project
  const { data: deviceInstances = [], isLoading: devicesLoading, error: instancesError } = useQuery({
    queryKey: ['deviceInstances', projectId, electricalPanelId, specificationId],
    queryFn: async () => {
      return await getProjectDevices(projectId, electricalPanelId, 'device', specificationId)
    },
    enabled: !!projectId
  })

  // На общей странице при поиске также ищем среди кабелей (у них тоже бывает position_label)
  const { data: cableInstances = [], isLoading: cablesLoading } = useQuery({
    queryKey: ['deviceInstancesCables', projectId],
    queryFn: () => getProjectDevices(projectId, undefined, 'cable'),
    enabled: !!projectId && showAllInstancesView,
  })

  const instances = useMemo(() => {
    if (!searchActive) return deviceInstances
    const byId = new Map<number, DeviceInstance>()
    deviceInstances.forEach((i) => byId.set(i.id, i))
    cableInstances.forEach((i) => byId.set(i.id, i))
    return Array.from(byId.values())
  }, [deviceInstances, cableInstances, searchActive])

  const instancesLoading = devicesLoading || (searchActive && cablesLoading)

  // Уникальные щиты для фильтра (из загруженных экземпляров)
  const panelFilterOptions = useMemo(() => {
    const source = searchActive ? instances : deviceInstances
    if (!showAllInstancesView || !source.length) return []
    const seen = new Map<number, string>()
    source.forEach((i) => {
      if (i.electrical_panel_id != null && i.panel_proj_id != null && !seen.has(i.electrical_panel_id)) {
        seen.set(i.electrical_panel_id, i.panel_proj_id)
      }
    })
    return Array.from(seen.entries()).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
  }, [showAllInstancesView, instances, deviceInstances, searchActive])

  // Фильтрация по поиску и щиту, затем сортировка
  const filteredAndSortedInstances = useMemo(() => {
    let list = instances
    if (showAllInstancesView) {
      const q = searchQuery.trim()
      if (q) {
        list = list.filter(
          (i) =>
            searchTextIncludes(String(i.id), q) ||
            searchTextIncludes(i.position_label || '', q) ||
            searchTextIncludes(i.spec_name || '', q) ||
            searchTextIncludes(i.panel_proj_id || '', q) ||
            searchTextIncludes(i.description || '', q)
        )
      }
      if (filterPanelId) {
        const pid = Number(filterPanelId)
        list = list.filter((i) => i.electrical_panel_id === pid)
      }
    }
    if (!list.length) return list
    return [...list].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortField) {
        case 'id':
          aValue = a.id
          bValue = b.id
          break
        case 'position_label':
          aValue = a.position_label || `${a.spec_name} #${a.instance_number}`
          bValue = b.position_label || `${b.spec_name} #${b.instance_number}`
          break
        case 'spec_name':
          aValue = a.spec_name || ''
          bValue = b.spec_name || ''
          break
        case 'panel_proj_id':
          aValue = a.panel_proj_id || ''
          bValue = b.panel_proj_id || ''
          break
        case 'description':
          aValue = a.description || ''
          bValue = b.description || ''
          break
        default:
          aValue = a[sortField]
          bValue = b[sortField]
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [instances, sortField, sortDirection, showAllInstancesView, searchQuery, filterPanelId])

  // Load available specifications for devices (filtered by electrical panel)
  const { data: specifications = [], isLoading: specificationsLoading, error: specificationsError } = useQuery({
    queryKey: ['specifications', projectId, electricalPanelId],
    queryFn: () => getSpecification(projectId, undefined, electricalPanelId),
    enabled: !!projectId && !!electricalPanelId && (showCreateForm || !!copyInstance)
  })

  const showPanelSelectorInCreateForm = showCreateForm && (!electricalPanelId || !!copyInstance)

  // Load electrical panels for edit and create/copy forms
  const { data: electricalPanels = [], isLoading: electricalPanelsLoading } = useQuery({
    queryKey: ['electricalPanels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: !!projectId && (showEditForm || showPanelSelectorInCreateForm)
  })

  // Create instance mutation
  const createInstanceMutation = useMutation({
    mutationFn: ({ projectId, specificationId, electricalPanelId, positionLabel, description, knxAddress }: {
      projectId: number; 
      specificationId: number; 
      electricalPanelId: number;
      positionLabel: string;
      description?: string;
      knxAddress?: string;
    }) => createDeviceInstance({
      specification_id: specificationId,
      project_id: projectId,
      electrical_panel_id: electricalPanelId,
      position_label: positionLabel,
      description: description,
      knx_address: knxAddress
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] })
      setShowCreateForm(false)
      setCopyInstance(null)
    },
    onError: (error) => {
      console.error('❌ Failed to create instance:', error)
      alert(`Ошибка создания экземпляра: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`)
    }
  })

  // Update instance mutation
  const updateInstanceMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<DeviceInstance> }) => {
      await updateDeviceInstance(data.id, data.updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] })
      setShowEditForm(false)
      setSelectedInstance(null)
    }
  })

  // Delete instance mutation
  const deleteInstanceMutation = useMutation({
    mutationFn: deleteDeviceInstance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] })
      setSelectedInstance(null)
    }
  })

  const handleCreateInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    const formData = new FormData(e.currentTarget)
    
    const specificationId = formData.get('specification_id') as string
    const positionLabel = formData.get('position_label') as string
    const description = formData.get('description') as string
    const knxAddress = formData.get('knx_address') as string
    
    // При копировании используем спецификацию из copyInstance
    const finalSpecificationId = copyInstance ? copyInstance.specification_id : specificationId
    
    if (!finalSpecificationId) {
      alert('Пожалуйста, выберите спецификацию устройства')
      return
    }
    
    const panelFromForm = formData.get('electrical_panel_id') as string
    const finalElectricalPanelId =
      electricalPanelId ??
      (panelFromForm ? parseInt(panelFromForm, 10) : null) ??
      createFormPanelId

    if (!finalElectricalPanelId) {
      alert('Электрический щит не выбран')
      return
    }
    
    const instanceData = {
      projectId: projectId,
      specificationId: parseInt(finalSpecificationId.toString()),
      electricalPanelId: finalElectricalPanelId,
      positionLabel: positionLabel || '',
      description: description || undefined,
      knxAddress: knxAddress || undefined
    }

    createInstanceMutation.mutate(instanceData)
  }

  const handleEditInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedInstance) return
    
    const formData = new FormData(e.currentTarget)
    
    const electricalPanelId = formData.get('electrical_panel_id') as string
    
    if (!electricalPanelId) {
      alert('Пожалуйста, выберите электрический щит')
      return
    }
    
    const updates = {
      position_label: (formData.get('position_label') as string || '').trim() || null,
      description: (formData.get('description') as string || '').trim() || null,
      knx_address: (formData.get('knx_address') as string || '').trim() || null,
      electrical_panel_id: parseInt(electricalPanelId),
      is_reserve: formData.get('is_reserve') ? 1 : 0
    }

    updateInstanceMutation.mutate({ id: selectedInstance.id, updates })
  }

  const handleEditClick = (instance: DeviceInstance) => {
    setSelectedInstance(instance)
    setShowEditForm(true)
  }

  const handleCopyClick = (instance: DeviceInstance) => {
    setCopyInstance(instance)
    setSelectedSpecificationId(instance.specification_id)
    setCreateFormPanelId(instance.electrical_panel_id ?? electricalPanelId ?? null)
    setShowCreateForm(true)
  }

  const handleDeleteClick = (instance: DeviceInstance) => {
    if (confirm(`Удалить экземпляр "${instance.position_label || instance.spec_name}"?`)) {
      deleteInstanceMutation.mutate(instance.id)
    }
  }

  if (instancesError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Экземпляры устройств</h2>
        <div className="text-red-600 bg-red-50 p-4 rounded-md">
          Ошибка загрузки экземпляров устройств: {instancesError instanceof Error ? instancesError.message : 'Неизвестная ошибка'}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">🔧 Экземпляры устройств</h2>
        {showAllInstancesView && (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Поиск по ID, обозн., щиту, описанию..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[200px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {panelFilterOptions.length > 0 && (
              <select
                value={filterPanelId}
                onChange={(e) => setFilterPanelId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все щиты</option>
                {panelFilterOptions.map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <button
          onClick={() => {
            setCopyInstance(null)
            setCreateFormPanelId(electricalPanelId ?? null)
            setShowCreateForm(true)
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          ➕ Добавить экземпляр
        </button>
      </div>

      {/* Loading state */}
      {instancesLoading && (
        <div className="text-center py-8 text-gray-500">Загрузка экземпляров устройств...</div>
      )}

      {/* Table for desktop and mobile */}
       {!instancesLoading && filteredAndSortedInstances.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader field="id">ID</SortableHeader>
                <SortableHeader field="position_label">Обозн.</SortableHeader>
                {!hideParentColumn && <SortableHeader field="spec_name">Родительский элемент</SortableHeader>}
                <SortableHeader field="panel_proj_id">Щит</SortableHeader>
                <SortableHeader field="description">Описание</SortableHeader>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
           {filteredAndSortedInstances.map((instance: DeviceInstance) => (
                <tr key={instance.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {instance.id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link 
                      to={getInstanceUrl(instance)}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {instance.position_label || `${instance.spec_name} #${instance.instance_number}`}
                    </Link>
                  </td>
                  {!hideParentColumn && (
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {instance.spec_name || '—'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {instance.panel_proj_id || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {instance.description || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => handleEditClick(instance)}
                        className="action-icon-btn edit"
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleCopyClick(instance)}
                        className="action-icon-btn copy"
                        title="Копировать"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleDeleteClick(instance)}
                        className="action-icon-btn delete"
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
                   </div>
      )}

      {/* Mobile responsive cards (shows below md breakpoint) */}
      {!instancesLoading && filteredAndSortedInstances.length > 0 && (
        <div className="md:hidden space-y-4">
          {filteredAndSortedInstances.map((instance: DeviceInstance) => (
            <div key={instance.id} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex justify-between items-start mb-2">
                <Link 
                  to={getInstanceUrl(instance)}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {instance.position_label || `${instance.spec_name} #${instance.instance_number}`} (ID: {instance.id})
                </Link>
                <div className="flex space-x-1">
                   <button
                     onClick={() => handleEditClick(instance)}
                    className="action-icon-btn edit"
                    title="Редактировать"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleCopyClick(instance)}
                    className="action-icon-btn copy"
                    title="Копировать"
                   >
                    📋
                   </button>
                   <button
                     onClick={() => handleDeleteClick(instance)}
                      className="action-icon-btn delete"
                      title="Удалить"
                   >
                    🗑️
                   </button>
                 </div>
               </div>
              {!hideParentColumn && (
                <div className="text-sm text-gray-600 mb-1">
                  Родительский элемент: {instance.spec_name || '—'}
                </div>
              )}
              <div className="text-sm text-gray-600 mb-1">
                Щит: {instance.panel_proj_id || '—'}
              </div>
              {instance.description && (
                <div className="text-sm text-gray-500 mb-2">
                  {instance.description}
                </div>
              )}
             </div>
           ))}
         </div>
       )}

      {/* Empty state */}
      {!instancesLoading && instances.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="mb-4">Экземпляры устройств для этого проекта не найдены.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Создать первый экземпляр
          </button>
        </div>
      )}
      {!instancesLoading && instances.length > 0 && filteredAndSortedInstances.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="mb-4">По вашему запросу ничего не найдено. Измените поиск или фильтр по щиту.</p>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">
              {copyInstance ? `Копировать экземпляр: ${copyInstance.position_label || copyInstance.spec_name}` : 'Создать экземпляр устройства'}
            </h3>
            {electricalPanelId && !copyInstance && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Электрический щит:</strong> #{electricalPanelId}
                </p>
              </div>
            )}
                        <form onSubmit={handleCreateInstance} className="space-y-4">
              {showPanelSelectorInCreateForm && (
                <div>
                  <label className="block text-sm font-medium mb-1">Электрический щит *</label>
                  {electricalPanelsLoading ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                      Загрузка щитов...
                    </div>
                  ) : (
                    <select
                      name="electrical_panel_id"
                      required
                      value={createFormPanelId ?? ''}
                      onChange={(e) => setCreateFormPanelId(e.target.value ? parseInt(e.target.value, 10) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Выберите электрический щит...</option>
                      {(electricalPanels as ElectricalPanel[]).map((panel: ElectricalPanel) => (
                        <option key={panel.id} value={panel.id}>
                          {panel.name} ({panel.proj_id})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              {!copyInstance && (
                <div>
                  <label className="block text-sm font-medium mb-1">Спецификация устройства</label>
                  {specificationsLoading ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                      Загрузка спецификаций...
                    </div>
                  ) : specificationsError ? (
                    <div className="w-full px-3 py-2 border border-red-300 rounded-md bg-red-50 text-red-600">
                      Ошибка загрузки спецификаций: {specificationsError instanceof Error ? specificationsError.message : 'Неизвестная ошибка'}
                    </div>
                  ) : (
                    <select
                      name="specification_id"
                      required
                      defaultValue=""
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      onChange={(e) => setSelectedSpecificationId(parseInt(e.target.value) || null)}
                    >
                      <option value="">Выберите спецификацию...</option>
                      {(specifications as Specification[]).map((spec: Specification) => (
                        <option key={spec.id} value={spec.id}>
                          {spec.name} ({spec.device_type_name || 'Неизвестный тип'})
                        </option>
                      ))}
                    </select>
                  )}
                  {!specificationsLoading && !specificationsError && (specifications as Specification[]).length === 0 && (
                    <div className="mt-2 text-sm text-orange-600">
                      Нет доступных спецификаций для электрического щита #{electricalPanelId}. Создайте спецификации устройств для этого щита сначала.
                    </div>
                  )}
                </div>
              )}


              
              <div>
                <label className="block text-sm font-medium mb-1">Позиционное обозначение</label>
                <input
                  type="text"
                  name="position_label"
                  defaultValue={copyInstance?.position_label || ''}
                  placeholder="напр., QF1, L1.1, S1.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание</label>
                <textarea
                  name="description"
                  defaultValue={copyInstance?.description || ''}
                  placeholder="Дополнительное описание экземпляра..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* KNX Address field - show for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
              {((copyInstance && (copyInstance.device_type_id === 14 || copyInstance.device_type_id === 5)) || 
                (!copyInstance && selectedSpecificationId && 
                 (() => {
                   const deviceTypeId = specifications.find((s: Specification) => s.id === selectedSpecificationId)?.device_type_id;
                   return deviceTypeId === 14 || deviceTypeId === 5;
                 })())) && (
                <div>
                  <label className="block text-sm font-medium mb-1">KNX адрес</label>
                  <input
                    type="text"
                    name="knx_address"
                    defaultValue={copyInstance?.knx_address || ''}
                    placeholder="напр., 1.1.1"
                    pattern="^\d+\.\d+\.\d+$"
                    title="Формат: 1.255.255"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Формат: 1.255.255</p>
                </div>
              )}

              <div className="flex space-x-2 pt-4">
                <button
                  type="submit"
                  disabled={createInstanceMutation.isPending}
                  className="flex-1 bg-green-600 text-white py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {createInstanceMutation.isPending ? 'Создание...' : (copyInstance ? 'Копировать' : 'Создать')}
                </button>
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedInstance && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Редактировать экземпляр устройства <span className="instance-id">#{selectedInstance.id}</span></h3>
            
            {/* Parent specification info - compact */}
            <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
              <span className="font-medium">Спецификация:</span> ID {selectedInstance.specification_id} • {selectedInstance.spec_name || '—'}
              {selectedInstance.manufacturer && <span> • {selectedInstance.manufacturer}</span>}
              {selectedInstance.model && <span> • {selectedInstance.model}</span>}
            </div>

            <form onSubmit={handleEditInstance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Позиционное обозначение</label>
                <input
                  type="text"
                  name="position_label"
                  defaultValue={selectedInstance.position_label || ''}
                  placeholder="напр., ДП-14.2-1, LD-1-1, УР-2-4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Электрический щит</label>
                {electricalPanelsLoading ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                    Загрузка щитов...
                  </div>
                ) : (
                  <select
                    name="electrical_panel_id"
                    required
                    defaultValue={selectedInstance.electrical_panel_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Выберите электрический щит...</option>
                    {(electricalPanels as ElectricalPanel[]).map((panel: ElectricalPanel) => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name} ({panel.proj_id})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание</label>
                <textarea
                  name="description"
                  defaultValue={selectedInstance.description || ''}
                  placeholder="Дополнительное описание экземпляра..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* KNX Address field - show for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
              {(selectedInstance.device_type_id === 14 || selectedInstance.device_type_id === 5) && (
                <div>
                  <label className="block text-sm font-medium mb-1">KNX адрес</label>
                  <input
                    type="text"
                    name="knx_address"
                    defaultValue={selectedInstance.knx_address || ''}
                    placeholder="напр., 1.1.1"
                    pattern="^\d+\.\d+\.\d+$"
                    title="Формат: 1.255.255"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Формат: 1.255.255</p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_reserve_device_instance"
                  name="is_reserve"
                  defaultChecked={Boolean(selectedInstance?.is_reserve)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_reserve_device_instance" className="ml-2 text-sm font-medium text-gray-700">
                  Резервный
                </label>
              </div>

              <div className="flex space-x-2 pt-4">
                <button
                  type="submit"
                  disabled={updateInstanceMutation.isPending}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
} 